import React from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { budgetService } from '../../services/endpoints/budgetService';
import { BudgetCategory, ShowBudgetItem, VialDetailItem } from '../../types';

// ── Category config ───────────────────────────────────────────────────────────

const CATEGORY_CONFIG: Record<BudgetCategory, { icon: string; label: string }> = {
  DrugLoad:  { icon: '💉', label: 'Drug Load'  },
  Travel:    { icon: '✈️',  label: 'Travel'     },
  Tanning:   { icon: '🟤', label: 'Tanning'    },
  EntryFees: { icon: '🏆', label: 'Entry Fees' },
  Coaching:  { icon: '👨‍🏫', label: 'Coaching'   },
  Other:     { icon: '📦', label: 'Other'      },
};

const CATEGORY_ORDER: BudgetCategory[] = [
  'DrugLoad', 'Travel', 'Tanning', 'EntryFees', 'Coaching', 'Other',
];

// ── Sub-components ────────────────────────────────────────────────────────────

interface CategoryRowProps {
  category: BudgetCategory;
  items: ShowBudgetItem[];
  showId: string;
  onDelete: (itemId: string) => void;
  isDeleting: boolean;
}

const CategoryRow: React.FC<CategoryRowProps> = ({
  category, items, onDelete, isDeleting,
}) => {
  const { icon, label } = CATEGORY_CONFIG[category];
  const categoryTotal = items.reduce((sum, i) => sum + i.amount, 0);
  const isEmpty = items.length === 0;

  return (
    <div className="border-b border-gray-700 last:border-0 py-3">
      <div className="flex justify-between items-start">
        <div className="flex items-center gap-2">
          <span className="text-lg">{icon}</span>
          <span className={`font-semibold text-sm ${isEmpty ? 'text-gray-600' : 'text-gray-200'}`}>
            {label}
          </span>
        </div>
        <span className={`text-sm font-bold ${isEmpty ? 'text-gray-600' : 'text-white'}`}>
          {isEmpty ? '— not set —' : `$${categoryTotal.toFixed(2)}`}
        </span>
      </div>

      {!isEmpty && (
        <div className="mt-2 space-y-3 pl-7">
          {items.map(item => {
            // Parse vial breakdown for DrugLoad items
            let vials: VialDetailItem[] = [];
            if (item.category === 'DrugLoad' && item.vialDetails) {
              try { vials = JSON.parse(item.vialDetails); } catch { /* ignore */ }
            }

            return (
              <div key={item.budgetItemId} className="group">
                {/* Label + amount row */}
                <div className="flex justify-between items-center">
                  <div className="flex-1 min-w-0">
                    <span className="text-xs text-gray-400 truncate">{item.label}</span>
                    {item.notes && (
                      <p className="text-xs text-gray-600 truncate">{item.notes}</p>
                    )}
                  </div>
                  <div className="flex items-center gap-3 ml-3 flex-shrink-0">
                    <span className="text-xs text-gray-300">${item.amount.toFixed(2)}</span>
                    <button
                      onClick={() => onDelete(item.budgetItemId)}
                      disabled={isDeleting}
                      className="text-gray-600 hover:text-red-400 opacity-0 group-hover:opacity-100 transition-opacity text-xs"
                      title="Remove item"
                    >
                      ✕
                    </button>
                  </div>
                </div>

                {/* Vial breakdown table (DrugLoad items only) */}
                {vials.length > 0 && (
                  <div className="mt-1.5 ml-1 rounded-lg border border-gray-700 overflow-hidden">
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="bg-gray-700/50">
                          <th className="text-left px-2 py-1 text-gray-400 font-medium">Compound</th>
                          <th className="text-center px-2 py-1 text-gray-400 font-medium">Vials</th>
                          <th className="text-right px-2 py-1 text-gray-400 font-medium">$/vial</th>
                          <th className="text-right px-2 py-1 text-gray-400 font-medium">Subtotal</th>
                        </tr>
                      </thead>
                      <tbody>
                        {vials.map((v, idx) => (
                          <tr key={idx} className="border-t border-gray-700/50">
                            <td className="px-2 py-1 text-gray-300">{v.shortName || v.compound}</td>
                            <td className="px-2 py-1 text-center text-gold-400 font-bold">{v.vials}</td>
                            <td className="px-2 py-1 text-right text-gray-400">${v.pricePerVial.toFixed(2)}</td>
                            <td className="px-2 py-1 text-right text-gray-300">${v.subtotal.toFixed(2)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {isEmpty && category !== 'DrugLoad' && (
        <p className="pl-7 mt-0.5 text-xs text-gray-700 italic">
          Future budget planner feature
        </p>
      )}
      {isEmpty && category === 'DrugLoad' && (
        <p className="pl-7 mt-0.5 text-xs text-gray-700 italic">
          Use the Purchase Calculator in PED Tracking to add estimates
        </p>
      )}
    </div>
  );
};

// ── Main component ─────────────────────────────────────────────────────────────

interface ShowBudgetProps {
  showId: string;
}

const ShowBudget: React.FC<ShowBudgetProps> = ({ showId }) => {
  const queryClient = useQueryClient();

  const { data: budget, isLoading, error } = useQuery({
    queryKey: ['show-budget', showId],
    queryFn: () => budgetService.getShowBudget(showId),
    enabled: !!showId,
  });

  const deleteMutation = useMutation({
    mutationFn: (itemId: string) => budgetService.deleteBudgetItem(showId, itemId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['show-budget', showId] }),
  });

  if (isLoading) {
    return (
      <div className="flex justify-center py-8">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-gold-500" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-red-400 text-sm text-center py-4">
        Failed to load budget data.
      </div>
    );
  }

  // Group items by category
  const grouped: Record<BudgetCategory, ShowBudgetItem[]> = {
    DrugLoad: [], Travel: [], Tanning: [], EntryFees: [], Coaching: [], Other: [],
  };
  (budget?.items ?? []).forEach(item => {
    if (item.category in grouped) {
      grouped[item.category as BudgetCategory].push(item);
    }
  });

  const total = budget?.totalEstimate ?? 0;
  const hasAnyItems = (budget?.items ?? []).length > 0;

  return (
    <div className="bg-gray-800 rounded-xl border border-gray-700 overflow-hidden">
      {/* Header */}
      <div className="flex justify-between items-center px-5 py-4 border-b border-gray-700">
        <h3 className="font-bold text-gold-500">💰 Show Budget Estimate</h3>
        {hasAnyItems && (
          <span className="text-xs text-gray-500">hover items to remove</span>
        )}
      </div>

      {/* Category rows */}
      <div className="px-5">
        {CATEGORY_ORDER.map(cat => (
          <CategoryRow
            key={cat}
            category={cat}
            items={grouped[cat]}
            showId={showId}
            onDelete={id => deleteMutation.mutate(id)}
            isDeleting={deleteMutation.isPending}
          />
        ))}
      </div>

      {/* Grand total */}
      <div className="px-5 py-4 bg-gray-750 border-t border-gray-700 flex justify-between items-center">
        <span className="font-bold text-white">Total Estimate</span>
        <span className={`text-xl font-bold ${hasAnyItems ? 'text-gold-400' : 'text-gray-600'}`}>
          {hasAnyItems ? `$${total.toFixed(2)}` : '$0.00'}
        </span>
      </div>

      {!hasAnyItems && (
        <div className="px-5 pb-4 text-center">
          <p className="text-xs text-gray-600">
            No budget items yet. Go to{' '}
            <span className="text-gold-600">PED Tracking → Planner</span>{' '}
            to calculate your drug load cost and save it here.
          </p>
        </div>
      )}
    </div>
  );
};

export default ShowBudget;
